import {i18n} from "src/boot/i18n";

export const MusicXmlImportSteps = ['PENDING', 'STARTED', 'FINGERING_FILLING', 'PENDING_JOB', 'MUSICXML_PARSING', 'HANDPRINT_PARSING', 'RHYTHMIC_PARSING', 'TIMELINE_GENERATION', 'COMPLETED', 'FAILED'] as const;
export type MusicXmlImportStep = typeof MusicXmlImportSteps[number];
export const MAX_STEP = MusicXmlImportSteps.length - 2;
export const MusicXmlImportProgression: Record<MusicXmlImportStep, number> = {
  PENDING: 0,
  STARTED: 1,
  FINGERING_FILLING: 2,
  PENDING_JOB: 3,
  MUSICXML_PARSING: 4,
  HANDPRINT_PARSING: 5,
  RHYTHMIC_PARSING: 6,
  TIMELINE_GENERATION: 7,
  COMPLETED: 8,
  FAILED: 8,
};

export interface MusicXmlImport {
  id: number;
  step: MusicXmlImportStep;
  failed_reason?: string | ErrorImportCodes;
  started_at?: string;
  completed_at?: string;
  published_version?: {
    id: number;
    piece: {
      url_artist: string;
      url_title: string
    }
  };
  version_id: number;
  readed_at?: string;
}

export const ErrorImportCodesArray = ['UNHANDLED_EXCEPTION', 'ISSUE_IN_MUSICXML_FILE', 'MULTI_VOICE_NOT_SUPPORTED', 'TUPLETS_NOT_SUPPORTED', 'MISSING_FINGERING', 'FILE_NOT_FOUND', 'NO_TIME_SIGNATURE', 'NOTE_NOT_IN_HANDPRINT', 'NO_HANDPRINT_FOUND', 'PIECE_ALREADY_EXISTS', 'INVALID_FILE_TYPE', 'NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT', 'CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS', 'CHORD_SPAN_VERY_SMALL', 'PINEAPPLE_API_ERROR', 'PINEAPPLE_API_NOT_ALL_FINGERINGS_RECEIVED'] as const;
export type ErrorImportCodes = typeof ErrorImportCodesArray[number];
interface ErrorMessagesContent extends Record<ErrorImportCodes, Object | null> {
  UNHANDLED_EXCEPTION: null,
  ISSUE_IN_MUSICXML_FILE: null,
  MULTI_VOICE_NOT_SUPPORTED: null,
  TUPLETS_NOT_SUPPORTED: null,
  MISSING_FINGERING: {
    hand: string,
    note_id: string,
    measure: number,
    start_division: number,
  },
  FILE_NOT_FOUND: null,
  NO_TIME_SIGNATURE: null,
  NO_HANDPRINT_FOUND: {
    hand: string,
    measure: number,
    division: number,
  },
  NOTE_NOT_IN_HANDPRINT: {
    hand: string,
    measure: number,
    division: number,
  },
  NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT: {
    handprint_id: number,
  }
  CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS: {
    handprint_id: number,
  }
  CHORD_SPAN_VERY_SMALL: {
    new_handprint_code: string,
    combine_fingering: number,
    measure: number,
  }
  INVALID_FILE_TYPE: null,
  PIECE_ALREADY_EXISTS: null,
  PINEAPPLE_API_ERROR: null,
  PINEAPPLE_API_NOT_ALL_FINGERINGS_RECEIVED: null,
}

export const getErrorMessage = (errorData: { status: ErrorImportCodes, content?: ErrorMessagesContent[ErrorImportCodes] }): string => {
  // Create a temporary Vue instance to access $t
  const defaultMessage = i18n.t(`importMusicxmlErrors.${errorData.status}`) as string;

  if (errorData.content) {
    const handLabel = i18n.t('importMusicxmlErrors.handLabel') as string;
    const measureLabel = i18n.t('importMusicxmlErrors.measureLabel') as string;
    const divisionLabel = i18n.t('importMusicxmlErrors.divisionLabel') as string;

    switch (errorData.status) {
      case 'NO_HANDPRINT_FOUND':
        return `${defaultMessage} (${handLabel} ${(errorData.content as ErrorMessagesContent['NO_HANDPRINT_FOUND']).hand}, ${measureLabel} ${(errorData.content as ErrorMessagesContent['NO_HANDPRINT_FOUND']).measure}, ${divisionLabel} ${(errorData.content as ErrorMessagesContent['NO_HANDPRINT_FOUND']).division})`;
      case 'NOTE_NOT_IN_HANDPRINT':
        return `${defaultMessage} (${handLabel} ${(errorData.content as ErrorMessagesContent['NOTE_NOT_IN_HANDPRINT']).hand}, ${measureLabel} ${(errorData.content as ErrorMessagesContent['NOTE_NOT_IN_HANDPRINT']).measure}, ${divisionLabel} ${(errorData.content as ErrorMessagesContent['NOTE_NOT_IN_HANDPRINT']).division})`;
      case 'MISSING_FINGERING':
        return `${defaultMessage} (${handLabel} ${(errorData.content as ErrorMessagesContent['MISSING_FINGERING']).hand}, ${measureLabel} ${(errorData.content as ErrorMessagesContent['MISSING_FINGERING']).measure}, ${divisionLabel} ${(errorData.content as ErrorMessagesContent['MISSING_FINGERING']).start_division})`;
      case 'NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT':
        return `${defaultMessage} (ID de l'empreinte: ${(errorData.content as ErrorMessagesContent['NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT']).handprint_id})`;
      case 'CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS':
        return `${defaultMessage} (ID de l'empreinte: ${(errorData.content as ErrorMessagesContent['CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS']).handprint_id})`;
      case 'CHORD_SPAN_VERY_SMALL':
        return `${defaultMessage} (Code de la nouvelle empreinte: ${(errorData.content as ErrorMessagesContent['CHORD_SPAN_VERY_SMALL']).new_handprint_code}, Combinaison de doigté: ${(errorData.content as ErrorMessagesContent['CHORD_SPAN_VERY_SMALL']).combine_fingering}, Mesure: ${(errorData.content as ErrorMessagesContent['CHORD_SPAN_VERY_SMALL']).measure})`;
      default:
        return defaultMessage;
    }
  }
  return defaultMessage;
};


